import React, { CSSProperties, useState } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import {
  BackLoginText,
  StyledOtp,
} from './VerifyOtp.styles';
import { authCode, resendOtp } from '../../api/paths';
import api from '../../api/base';
import { LoginResponseType, OtherLoginMethod } from '../../api/paths.types';
import { useDispatch } from 'react-redux';
import { setUserTokens } from '../../redux/slices/userProfile';
import { useNavigate } from 'react-router-dom';
import { addSnackbar } from '../../redux/slices/snackbar';
import { LoginButton, LoginFormWrapper } from '../../pages/Login/Login.styles';
import { ReactComponent as ArrowLeft } from '../../assets/icons/base/arrow-left.svg';
import { ReactComponent as MailIcon } from '../../assets/icons/base/mail.svg';
import { ReactComponent as SMSIcon } from '../../assets/icons/base/message-square.svg';
import { ReactComponent as SmartphoneIcon } from '../../assets/icons/base/smartphone.svg';
import moment from 'moment';
import useError from '../../hooks/useError';
import { CustomOutlinedButton } from '../common/Button';

interface VerifyOtpProps {
  authData: LoginResponseType;
  setAcknowledgeUserPopup: () => void;
  backToLoginScreen: () => void;
}

interface VerifyOtpPost {
  ephemeral_token: string;
}

const VerifyOtp = (props: VerifyOtpProps) => {
  const { authData, setAcknowledgeUserPopup, backToLoginScreen } = props;
  const { method, ephemeral_token, other_methods } = authData;

  const [loading, setLoading] = useState<boolean>(false);
  const [otpInput, setOtpInput] = useState<string>('');
  const [disableButton, setDisableButton] = useState<boolean>(false);
  const [lastResend, setLastResnd] = useState<string>('');

  const [handleError] = useError();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const setOtpInputHandler = (value: string) => {
    setOtpInput(value);
    setDisableButton(false);
  };

  const handleOtpResend = async (resendMethod?: string) => {
    if (lastResend === '' || moment.duration(moment().diff(moment(lastResend))).asSeconds() > 30) {
      try {
        await api.post<VerifyOtpPost>(resendOtp, { ephemeral_token, method: resendMethod }, { headers: { authorization: '' } });
        setLastResnd(moment().toISOString());
        dispatch(addSnackbar({ message: 'Two-factor authentication code successfully resent.' }));
      } catch (error: any) {
        handleError(error);
      }
    } else {
      const passedSeconds = moment.duration(moment().diff(moment(lastResend))).asSeconds();
      dispatch(addSnackbar({ message: `Two-factor authentication code resent too soon. Please wait ${Math.round(30 - passedSeconds)} more seconds.` }));
    }
  };

  const handleVerifySms = async (e: any) => {
    e.preventDefault();

    setLoading(true);
    try {
      const response = await api.post(authCode, { code: otpInput, ephemeral_token }, { headers: { authorization: '' } });
      const { data: { access, refresh, token, ws, uid } } = response;

      // If user password expired we won't get access token
      // We will only get info to update password
      // We are doing this flow because we don't want to interrupt user while he already logged in
      // He should do it only on login
      if (uid) {
        navigate(`/reset-password-confirm?uid=${uid}&token=${token}`);
      } else {
        dispatch(setUserTokens({ access, refresh, ws }));
        setAcknowledgeUserPopup();
      }
    } catch (error: any) {
      handleError(error);
      setLoading(false);
    }
  };

  const getIcon = (methodName: string, style: CSSProperties) => {
    if (methodName === 'sms') {
      return <SMSIcon style={style}/>;
    }

    if (methodName === 'email') {
      return <MailIcon style={style}/>;
    }

    if (methodName === 'push') {
      return <SmartphoneIcon style={style}/>;
    }

    return null;
  };

  const buildOtherOptionsButton = (methodItem: OtherLoginMethod) => {
    const iconStyle = {
      width: '20px',
      height: '20px',
      stroke: '#86A9D4',
    };

    return <CustomOutlinedButton onClick={() => handleOtpResend(methodItem.name)}>
      <Typography fontWeight={500}
        color={'#86A9D4'}
        display={'flex'}
        alignItems={'center'}
        gap={'8px'}>
        {getIcon(methodItem.name, iconStyle)}
        {methodItem.description}
      </Typography>
    </CustomOutlinedButton>;
  };


  return (
    <LoginFormWrapper onSubmit={handleVerifySms}>
      <Grid item xs={12} gap="8px">
        <Typography
          variant="dsm"
          color={(t) => t.palette.base.bodyText}
          fontWeight="bold"
          component="p"
        >
        Please enter your 2FA Code
        </Typography>
        <Typography
          variant="tmd"
          color={(t) => t.palette.grey[500]}
          component="p"
        >
        Two-factor authentication code is sent on your {method}.
        </Typography>
      </Grid>
      <Box width="100%" marginTop={'32px'}>
        <Grid item width={'100%'}>
          <StyledOtp
            numInputs={6}
            isInputNum
            value={otpInput}
            inputStyle={'optInput'}
            onChange={setOtpInputHandler}
            placeholder="000000"
            shouldAutoFocus
          />
        </Grid>
        <Grid
          width={'100%'}
          item
          mt={'24px'}
        >
          <LoginButton
            disabled={disableButton || otpInput.trim() === ''}
            variant="contained"
            color="primary"
            fullWidth
            type="submit"
            loading={loading}
            loadingPosition="end"
            size="large"
          >
          Verify
          </LoginButton>
        </Grid>
      </Box>
      <Grid
        width={'100%'}
        item
        xs={12}
        mt={'24px'}
        display={'flex'}
        justifyContent={'center'}
      >
        <Typography variant="tsm"
          color={(t) => t.palette.grey[600]}
          fontWeight={500}
          component="span">
        Didn’t receive the code? {' '}
        </Typography>
        &nbsp;
        <Typography
          variant="tsm"
          color={(t) => t.palette.primary.main}
          fontWeight={500}
          onClick={() => handleOtpResend()}
          sx={{ cursor: 'pointer' }}
        >
          Resend code
        </Typography>
      </Grid>
      {other_methods.length > 0 && <Grid width={'100%'}
        item
        xs={12}
        gap={'16px'}
        mt={'16px'}
        display={'flex'}
        flexDirection={'column'}>
        <Typography variant="tsm"
          color={(t) => t.palette.grey[600]}
          fontWeight={500}
          component="span"
          textAlign={'center'}
          sx={{
            opacity: '0.5',
          }}>OR</Typography>
        {other_methods.map((methodItem) => buildOtherOptionsButton(methodItem))}
      </Grid> }
      <Grid
        width={'100%'}
        item
        xs={12}
        mt={'32px'}
      >
        <BackLoginText
          onClick={backToLoginScreen}
          variant="tsm"
        >
          <ArrowLeft />
        Back to login
        </BackLoginText>
      </Grid>
    </LoginFormWrapper>
  );
};

export default VerifyOtp;
